import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';

import { ShowChart as ChartIcon, List as ListIcon } from '@mui/icons-material';

import { TextField, MenuItem, Button } from '@mui/material';
import { DatePicker, TimePicker, MobileTimePicker } from '@mui/x-date-pickers';

import Tile from '../../../../common/components/Tile/Tile';
import Chart from '../../../../common/components/Chart/Chart';
import TableEnhancedContainer from '../../../../common/components/TableEnhanced/TableEnhancedContainer';
import Loading from '../../../../common/components/Loading/Loading';
import Constants from '../../../../common/constants/Constants';
import DialogWrapper from '../../../../common/components/DialogWrapper/DialogWrapper';
import Subreport from '../InstrumentTelemetry/components/Subreport/Subreport';
import ChartEnhanced from '../../../../common/components/ChartEnhanced/ChartEnhanced';
import './InstrumentTelemetry.css';

import moment from 'moment';

const filterSpacingStyle = { width: '20%', marginRight: '2rem' };

const Filters = (props) => (
  <Tile
    className="instrument-telemetry__filters u-mb-full"
    title="Filters"
    helpTag={`instrument_telemetry-filter-${props.currentReportName}`}
  >
    <div className="u-fx u-fx-align-center u-mb-half">
      {props.showTableFilter ? (
        <TextField
          variant="standard"
          select={true}
          onChange={(event, index, value) => props.onChangeSourceTable(index.props.id)}
          label="Type"
          value={props.selectedTableFilter.displayName}
          margin="normal"
          data-qa="qa-telemetry-navigate"
        >
          {props.tableFilterList.map((el, index) => (
            <MenuItem key={index} value={el.displayName} id={index}>
              {el.displayName}
            </MenuItem>
          ))}
        </TextField>
      ) : null}
    </div>
    <div className="u-fx u-fx-align-end">
      <DatePicker
        slotProps={{
          textField: () => ({
            'data-qa': 'qa-date-from',
            error: props.dateTimeErrorMessage,
          }),
          actionBar: {
            // The actions will be the same between desktop and mobile
            actions: ['cancel', 'accept'],
          },
        }}
        className="u-mr-half"
        closeOnSelect={false}
        style={filterSpacingStyle}
        label="Date from"
        value={props.dateTimeFromFilter}
        placeholder="Date from"
        maxDate={moment.utc().add(1, 'months').startOf('month')}
        onAccept={(dateTime) => props.onChangeDateTime('dateFromFilter', dateTime)}
        openTo={'year'}
        format={'YYYY-MM-DD'}
      />
      <MobileTimePicker
        slotProps={{
          textField: () => ({
            'data-qa': 'qa-time-from',
            error: props.dateTimeErrorMessage,
          }),
        }}
        className="u-mr-half"
        style={filterSpacingStyle}
        ampm={false}
        label="Time from"
        value={props.dateTimeFromFilter}
        onAccept={(dateTime) => props.onChangeDateTime('timeFromFilter', dateTime)}
      />
      <DatePicker
        slotProps={{
          textField: () => ({
            'data-qa': 'qa-date-to',
            error: props.dateTimeErrorMessage,
          }),
          actionBar: {
            // The actions will be the same between desktop and mobile
            actions: ['cancel', 'accept'],
          },
        }}
        className="u-mr-half"
        closeOnSelect={false}
        style={filterSpacingStyle}
        label="Date to"
        value={props.dateTimeToFilter}
        placeholder="Date to"
        maxDate={moment.utc().add(1, 'months').startOf('month')}
        onAccept={(dateTime) => props.onChangeDateTime('dateToFilter', dateTime)}
        openTo={'year'}
        format={'YYYY-MM-DD'}
      />
      <MobileTimePicker
        slotProps={{
          textField: () => ({
            'data-qa': 'qa-time-to',
            error: props.dateTimeErrorMessage,
          }),
        }}
        className="u-mr-half"
        ampm={false}
        label="Time to"
        value={props.dateTimeToFilter}
        onAccept={(dateTime) => props.onChangeDateTime('timeToFilter', dateTime)}
      />
    </div>
    <div>
      <p className="u-italic" style={{ color: Constants.COLORS.WARNING }}>
        {props.timeRangeMessage}
      </p>
    </div>
    {props.dateTimeErrorMessage ? (
      <div className="u-fx u-fx-align-end u-mt-full ">
        <p className="u-italic" style={{ color: Constants.COLORS.ERROR }}>
          {props.dateTimeErrorMessage}
        </p>
      </div>
    ) : null}

    <div className="u-mt-double u-fx u-fx-align-center">
      <Button
        variant="contained"
        onClick={props.onApplyFilters}
        color="primary"
        disabled={props.isFetching}
        data-qa="qa-apply-filters"
      >
        Apply
      </Button>
      {props.metadata ? (
        <p className="u-ml-double u-italic" style={{ color: Constants.COLORS.WARNING }}>
          {props.metadata.Message}
        </p>
      ) : null}
    </div>
  </Tile>
);

class InstrumentTelemetry extends PureComponent {
  render() {
    return (
      <div className="instrument-telemetry">
        <DialogWrapper
          onClickCancel={this.props.subreportConfig.dialogSubreport.onClose}
          open={this.props.subreportConfig.dialogSubreport.open}
          title="Chart"
          hideMainAction={true}
          maxWidth={'xl'}
        >
          <Subreport subreportParams={this.props.subreportConfig.dialogSubreport.args} />
        </DialogWrapper>
        <Filters
          tableFilterList={this.props.tableFilterList}
          selectedTableFilter={this.props.selectedTableFilter}
          showTableFilter={this.props.showTableFilter}
          onChangeSourceTable={this.props.onChangeSourceTable}
          dateTimeFromFilter={this.props.dateTimeFromFilter}
          dateTimeToFilter={this.props.dateTimeToFilter}
          onChangeDateTime={this.props.onChangeDateTime}
          timeRangeMessage={this.props.timeRangeMessage}
          onApplyFilters={this.props.onApplyFilters}
          metadata={this.props.metadata}
          isFetching={this.props.isFetching}
          currentReportName={this.props.currentReportName}
          navigatorConfig={this.props.navigatorConfig}
          dateTimeErrorMessage={this.props.dateTimeErrorMessage}
        />
        <Loading condition={this.props.isFetching}>
          {this.props.chartData.Configuration ? (
            <Tile
              title="Chart"
              TitleIcon={ChartIcon}
              className="instrument-telemetry u-mb-full"
              helpTag={`instrument_telemetry-chart-${this.props.currentReportName}`}
            >
              <ChartEnhanced
                config={this.props.chartData.Configuration}
                metadata={this.props.metadata}
                dateTimeFrom={this.props.dateTimeFromFilter}
                dateTimeTo={this.props.dateTimeToFilter}
                onChangeDateTime={this.props.onChangeDateTime}
                activeTimeGran={this.props.activeTimeGran}
                dateTimeError={this.props.dateTimeErrorMessage != null ? true : false}
                maxDateTo={moment.utc().add(1, 'months').startOf('month')}
              />
            </Tile>
          ) : null}
          <Tile
            title="Table"
            TitleIcon={ListIcon}
            className="instrument-telemetry u-mb-full"
            helpTag={`instrument_telemetry-table-${this.props.currentReportName}`}
          >
            <TableEnhancedContainer
              columns={this.props.tableConfig}
              data={this.props.tableData}
              enableHideColumns={true}
              enableActionButtons={true}
              exportRawDataReport={this.props.exportRawDataReport}
              instrumentInfo={this.props.instrument.SerialNumber}
              extraActionsButtons={this.props.extraActionsButtons}
              dateTimeFrom={this.props.dateTimeFromFilter}
              dateTimeTo={this.props.dateTimeToFilter}
            />
          </Tile>
        </Loading>
      </div>
    );
  }
}

InstrumentTelemetry.propTypes = {
  tableFilterList: PropTypes.array,
  selectedTableFilter: PropTypes.object,
  showTableFilter: PropTypes.bool,
  onChangeSourceTable: PropTypes.func,
  dateTimeFromFilter: PropTypes.object,
  dateTimeToFilter: PropTypes.object,
  onChangeDateTime: PropTypes.func,
  timeRangeMessage: PropTypes.string,
  onApplyFilters: PropTypes.func,
  chartData: PropTypes.object,
  tableConfig: PropTypes.array,
  tableData: PropTypes.array,
  metadata: PropTypes.object,
  isFetching: PropTypes.bool,
  exportRawDataReport: PropTypes.string,
};

InstrumentTelemetry.defaultProps = {
  tableFilterList: [
    {
      displayName: '',
      value: '',
    },
  ],
  selectedTableFilter: {
    displayName: '',
    value: '',
  },
  showTableFilter: true,
  onChangeSourceTable: () => {},
  dateTimeFromFilter: {},
  dateTimeToFilter: {},
  timeRangeMessage: null,
  onChangeDateTime: () => {},
  onApplyFilters: () => {},
  chartData: {},
  tableConfig: [],
  tableData: [],
  metadata: {},
  isFetching: false,
  exportRawDataReport: '',
};

export default InstrumentTelemetry;
